import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { MousePointer2, Hand, HandMetal } from 'lucide-react'
import {
  CanvasWindowRestoreIcon, CanvasPointerClickIcon,
  CanvasAreaIcon, CanvasNoteIcon, CanvasMemoIcon, CanvasTextIcon,
} from './icons'
import { CanvasApplyTasksIcon } from './icons/CanvasApplyTasksIcon'
import { growMotion } from '@/src/renderer/lib/motion/growMotion'
import type { CanvasToolbarProps } from './CanvasToolbar.types'
import type { CanvasTool } from './ProjectCanvas.types'
import './CanvasToolbar.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
  Karenzzeit nach dem Ablegen der Pille: So lange bleibt der Griff-Tooltip noch
  zu. Die Pille landet unter dem Zeiger — ohne Pause spränge der Tooltip sofort
  auf, obwohl man gerade erst losgelassen hat.
*/
export const GRIP_TOOLTIP_GRACE_MS = 1000

/**
  So lange steht `--swapping` an der Pille: das längste Stück des Aufspringens — der vierte Strahl des Zeigers,
  150 ms verzögert und 360 ms lang (`_toolbarIconMotion.scss`) — und etwas Luft.
*/
export const TOOL_SWAP_POP_MS = 560

/*
  Das Übernehmen-Segment wächst nach unten heraus — dieselbe Bewegung wie ein
  neuer Knopf in der Werkzeugzeile einer aufgeklappten Aufgabe, nur auf der
  anderen Achse. Die Staffelung und ihr Grund stehen in `lib/motion/growMotion.ts`.

  Zwei Dinge sind hier eigen:

  • `size: 'auto'` statt einer Zahl — das Segment besteht aus Trenner und Knopf
    und misst sich selbst.
  • Dass die Pille **aus der Mitte** länger wird, kostet keine Zeile: Ihre
    Platte steht auf `top: 0; bottom: 0; margin-block: auto`
    (ProjectCanvasPanel.scss), die Mitte wandert also von selbst um die halbe
    Zuwachshöhe nach oben.
*/
const {
  initial: applySegmentInitial,
  animate: applySegmentEnter,
  exit: applySegmentExit,
  style: applySegmentStyle,
} = growMotion({ axis: 'height', size: 'auto' })

/**
 * The vertical pill toolbar for the Projekt-Canvas (Figma: "Canvas Toggle Long").
 * Two stacked groups separated by a hairline divider: the navigation group on
 * top — Canvas-Toggle when closed, Select/Hand once open — and the placement
 * tools below (Notiz, Memo, Text, Bereich). `variant` swaps the top group; die
 * Werkzeug-Gruppe trägt **Text** und **Bereich** nur im geöffneten Canvas —
 * geschlossen stehen dort allein die beiden Zettel-Arten. `peek` (der
 * **Canvas-Anriss**) hat gar keine obere Gruppe — nur die Werkzeuge.
 *
 * @example
 * <CanvasToolbar variant="entry" tool="select" onOpenPanel={...} onStartPlacingSticky={...} onToolChange={...} />
 */
/** Abstand der Tooltips zum Knopf: die Polsterung der Pille (8px) und Luft dahinter. */
const TOOLTIP_OFFSET = 16

export const CanvasToolbar = React.forwardRef<HTMLDivElement, CanvasToolbarProps>(function CanvasToolbar({
  variant, tool, onOpenPanel, onStartPlacingSticky, onStickyPointerDown, onStartPlacingMemo, onMemoPointerDown,
  onStartPlacingText, onTextPointerDown, onStartPlacingArea, onAreaPointerDown, onToolChange, onResetZoom,
  onGripPointerDown, gripPressed = false, applyCount = 0, onApplyTasks, side = 'right', className,
}, ref) {
  const { t } = useTranslation()
  // Griff-Tooltip: aus beim Ziehen und noch eine Karenzzeit nach dem Ablegen.
  const [gripQuiet, setGripQuiet] = useState(false)
  useEffect(() => {
    if (gripPressed) {
      setGripQuiet(true)
      return
    }
    const id = setTimeout(() => setGripQuiet(false), GRIP_TOOLTIP_GRACE_MS)
    return () => clearTimeout(id)
  }, [gripPressed])
  /*
    **Zeiger und Hand federn beim Tausch** (#315): Das neue Icon springt aus 60 % auf — nur beim Wechsel, nicht
    beim Öffnen der Pille, und `--swapping` steht nur so lange, wie das Aufspringen dauert: Ein Tab, der aus
    `display: none` zurückkommt, startet seine Animationen von vorn (wie `CanvasTape`, Code-Review 11.10.2026).
  */
  const [lastTool, setLastTool] = useState(tool)
  const [swapping, setSwapping] = useState(false)
  if (tool !== lastTool) {
    setLastTool(tool)
    setSwapping(true)
  }
  useEffect(() => {
    if (!swapping) return
    const id = setTimeout(() => setSwapping(false), TOOL_SWAP_POP_MS)
    return () => clearTimeout(id)
  }, [swapping, lastTool])
  /*
    Auf dem Button unter dem Zeiger bleibt das neue Icon still, bis der Zeiger ihn verlässt — sonst liefe seine
    Bewegung beim Überfahren mit dem Aufspringen zusammen (`--swapped`). Gefragt wird der Zeiger, nicht der Weg:
    Klick und V/H über dem Button gleich, Tab und Enter ohne Zeiger darauf nicht (Code-Review 11.10.2026).
  */
  const navButtons = useRef<Partial<Record<CanvasTool, HTMLButtonElement | null>>>({})
  const [swappedUnder, setSwappedUnder] = useState<CanvasTool | null>(null)
  useLayoutEffect(() => {
    if (!swapping) return
    const tools: CanvasTool[] = ['select', 'hand']
    setSwappedUnder(tools.find(own => navButtons.current[own]?.matches(':hover')) ?? null)
  }, [swapping, lastTool])
  const navButtonClass = (own: CanvasTool) => [
    'canvas-toolbar__button',
    tool === own && 'canvas-toolbar__button--tool-active',
    swappedUnder === own && 'canvas-toolbar__button--swapped',
  ].filter(Boolean).join(' ')
  const leaveNavButton = (own: CanvasTool) => swappedUnder === own ? () => setSwappedUnder(null) : undefined

  // Tooltips stehen NEBEN der Pille, zur Fläche hin — über ihr deckten sie die Nachbarknöpfe zu.
  const tooltipSide = side === 'left' ? 'right' : 'left'
  // Im **Canvas-Anriss** gibt es nur die Werkzeug-Gruppe: kein Öffnen-Knopf
  // (den tragen Streifenfläche und Maximieren-Knopf) und damit auch keine
  // Trennlinie, die etwas trennen könnte.
  const hasNavGroup = variant !== 'peek'

  // Nur geöffnet: Ausgewählt wird auf der Fläche. Und nur mit Handler — ein
  // Knopf ohne Ziel wäre eine Lüge.
  const showApply = variant === 'full' && applyCount > 0 && Boolean(onApplyTasks)

  const rootClass = [
    'canvas-toolbar',
    // Trägt den Rundungswechsel der Werkzeug-Gruppe im Takt der Bewegung
    // (CanvasToolbar.scss). Ohne ihn wäre die Pille unten eckig, solange das
    // Segment darunter noch wächst.
    showApply && 'canvas-toolbar--has-apply',
    swapping && 'canvas-toolbar--swapping',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div ref={ref} className={rootClass}>
      {hasNavGroup && (
      <div className="canvas-toolbar__group canvas-toolbar__group--nav">
        {/*
          Der **Griff** (Figma „Toolbar Drag-Handle", 5095:52140): Daran wird
          die Pille auf die andere Seite gezogen. Nur geöffnet — geschlossen
          hat sie keine Gegenseite (CONTEXT.md, Werkzeug-Pille). Ein reines
          Mauswerkzeug wie die Options-Leiste, deshalb kein Knopf und kein
          Tab-Halt: Ein fokussierbarer Griff, den die Tastatur nicht bewegen
          kann, verspräche etwas.
        */}
        {variant === 'full' && (
          // Beim Ziehen und kurz nach dem Ablegen kein Tooltip (`gripQuiet`). Ohne Text hält `WithTooltip` ihn zu.
          <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={gripPressed || gripQuiet ? undefined : t('projectCanvas.toolbar.moveToolbar')}>
            <div
              className={`canvas-toolbar__grip${gripPressed ? ' canvas-toolbar__grip--pressed' : ''}`}
              onPointerDown={onGripPointerDown}
              draggable={false}
            />
          </WithTooltip>
        )}

        {/*
          Figma `btn Canvas Toolbar Expand` (4916:265209): dasselbe Zeichen wie
          das linke Segment der Fenster-Umschaltgruppe im maximierten Canvas.
          Als EINZIGES Zeichen der Pille in 20px statt 24px, wie im Entwurf
          (entschieden 2026-09-13). Der Knopf bleibt 32×32 — `--open` gleicht
          das Polster aus, damit die Pille nicht in der Höhe springt.
        */}
        {variant === 'entry' && (
          // `⇧C` öffnet ebenfalls geteilt (`useCanvasPanelShortcut`) — wie dieser Knopf.
          <WithTooltip
            side={tooltipSide}
            sideOffset={TOOLTIP_OFFSET}
            content={t('projectCanvas.toolbar.open')}
            shortcut={{ kind: 'character', text: 'C', shift: true }}
          >
            <button
              aria-label={t('projectCanvas.toolbar.open')}
              type="button"
              className="canvas-toolbar__button canvas-toolbar__button--open"
              onClick={onOpenPanel}
              data-intro-anchor="board"
            >
              <CanvasWindowRestoreIcon size={20} />
            </button>
          </WithTooltip>
        )}

        {variant === 'full' && (
          <>
            {/*
              Beide Navigations-Werkzeuge tauschen im aktiven Zustand ihr Icon
              (Figma `btn Canvas Toolbar Pointer` 4916:265183 und
              `btn Canvas Toolbar Hand` 4916:265188, je Variant Active/Inactive):
              aus dem schlichten Pfeil wird der Pfeil mit den vier
              Klick-Strahlen, aus der offenen Hand die greifende.
              Dazu `--tool-active` — ein heller Violet-Chip, nicht das
              deckende Brand-Violet des generischen `--active`.

              Die Hülle `__swap` wechselt mit dem Icon (`key`): Eine neue Hülle
              federt auf, solange `--swapping` an der Pille steht (#315).
            */}
            <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.select')} shortcut={{ kind: 'character', text: 'V' }}>
              <button
                aria-label={t('projectCanvas.toolbar.select')}
                type="button"
                ref={el => { navButtons.current.select = el }}
                className={navButtonClass('select')}
                onClick={() => onToolChange('select')}
                onPointerLeave={leaveNavButton('select')}
              >
                <span key={tool === 'select' ? 'active' : 'idle'} className="canvas-toolbar__swap">
                  {tool === 'select'
                    ? <CanvasPointerClickIcon size={24} />
                    : <MousePointer2 size={24} strokeWidth={1.5} className="canvas-toolbar__icon--pointer" />}
                </span>
              </button>
            </WithTooltip>
            <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.pan')} shortcut={{ kind: 'character', text: 'H' }}>
              <button
                aria-label={t('projectCanvas.toolbar.pan')}
                type="button"
                ref={el => { navButtons.current.hand = el }}
                className={navButtonClass('hand')}
                onClick={() => onToolChange('hand')}
                onPointerLeave={leaveNavButton('hand')}
                // Doppelklick stellt die 1:1-Ansicht her, wie ⇧0. Die beiden
                // einfachen Klicks feuern dabei mit — das Werkzeug landet
                // ohnehin auf „hand", der zweite Aufruf ist folgenlos.
                onDoubleClick={onResetZoom}
              >
                <span key={tool === 'hand' ? 'active' : 'idle'} className="canvas-toolbar__swap">
                  {tool === 'hand'
                    ? <HandMetal size={24} strokeWidth={1.5} className="canvas-toolbar__icon--hand-grab" />
                    : <Hand size={24} strokeWidth={1.5} className="canvas-toolbar__icon--hand" />}
                </span>
              </button>
            </WithTooltip>
          </>
        )}
      </div>
      )}

      {hasNavGroup && (
      <div className="canvas-toolbar__divider">
        <span className="canvas-toolbar__divider-line" />
      </div>
      )}

      <div className="canvas-toolbar__group canvas-toolbar__group--tools">
        <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.placeNote')} shortcut={{ kind: 'character', text: 'N' }}>
          <button
            aria-label={t('projectCanvas.toolbar.placeNote')}
            type="button"
            className="canvas-toolbar__button canvas-toolbar__button--note"
            onClick={onStartPlacingSticky}
            // Anfassen startet einen Zug: Am Zeiger hängt sofort eine Notiz,
            // das Loslassen legt ihn ab. Der Klick bleibt daneben bestehen — wer
            // nur antippt, stellt wie bisher scharf.
            onPointerDown={onStickyPointerDown}
            // Sonst startete der Browser sein eigenes Zieh-Bild und überlagerte
            // unser Vorschaubild.
            draggable={false}
          >
            <CanvasNoteIcon size={24} />
          </button>
        </WithTooltip>
        <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.placeMemo')} shortcut={{ kind: 'character', text: 'M' }}>
          <button
            aria-label={t('projectCanvas.toolbar.placeMemo')}
            type="button"
            className="canvas-toolbar__button canvas-toolbar__button--memo"
            onClick={onStartPlacingMemo}
            // Dieselben zwei Gesten wie am Notiz-Knopf (CONTEXT.md,
            // Werkzeug-Pille): Antippen stellt scharf, ein Zug trägt die Notiz.
            onPointerDown={onMemoPointerDown}
            draggable={false}
          >
            <CanvasMemoIcon size={24} />
          </button>
        </WithTooltip>
        {/*
          **Text** und **Bereich** nur im GEÖFFNETEN Canvas.

          Geschlossen — als **Anriss** wie als freie Pille — zeigt sie die
          beiden ZETTEL-Arten und sonst nichts (CONTEXT.md, Werkzeug-Pille).
          Zwei Gründe, und sie zeigen in dieselbe Richtung:

          **Platz.** Im Streifen sitzt die Pille senkrecht mittig, und darunter
          beginnt der Bereich der Zettel. Mit vier Knöpfen wuchs sie um 72px
          nach unten und schob ihn hinaus; `$peek-toolbar-height` in
          `CanvasPeek.scss` rechnet ohnehin mit ZWEI Knöpfen, die beiden
          zusätzlichen liefen also über ihre eigene gemeldete Höhe hinaus.

          **Sinn.** Geschlossen wirft man etwas hin: eine Notiz, eine Memo. Ein
          **Bereich** fasst zusammen, was schon da liegt, und ein **Text** legt
          sich in eine Fläche — beides will man sehen, während man es tut. Wer
          das vorhat, öffnet den Canvas ohnehin.
        */}
        {variant === 'full' && (
          <>
            {/*
              Das **Text**-Werkzeug (Figma `btn Canvas Toolbar Text` 5153:96153),
              zwischen Memo und Bereich. Es steht in der Pille, bevor es etwas tut
              — wie seinerzeit Notiz und Bereich: bedienbar, nicht ausgegraut; der
              Knopf feuert, sobald es einen Handler gibt. Das Kürzel T steht in
              der Beschriftung und wird mit der Funktion scharf.
            */}
            <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.placeText')} shortcut={{ kind: 'character', text: 'T' }}>
              <button
                aria-label={t('projectCanvas.toolbar.placeText')}
                type="button"
                className="canvas-toolbar__button canvas-toolbar__button--text"
                onClick={onStartPlacingText}
                onPointerDown={onTextPointerDown}
                draggable={false}
              >
                <CanvasTextIcon size={24} />
              </button>
            </WithTooltip>
            {/*
              Der **Bereich** (Figma `btn Canvas Toolbar Section` 5136:46218).
              Dieselben zwei Gesten wie an Notiz und Memo; auf der Fläche
              legt ein Klick die Standardgröße ab, ein Zug zieht ihn auf.
            */}
            <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.toolbar.placeArea')} shortcut={{ kind: 'character', text: 'S', shift: true }}>
              <button
                aria-label={t('projectCanvas.toolbar.placeArea')}
                type="button"
                className="canvas-toolbar__button canvas-toolbar__button--area"
                onClick={onStartPlacingArea}
                onPointerDown={onAreaPointerDown}
                draggable={false}
              >
                <CanvasAreaIcon size={24} />
              </button>
            </WithTooltip>
          </>
        )}
      </div>

      {/*
        Das **Übernehmen**-Segment (Figma 4916:265109): Trenner und Knopf
        wachsen gemeinsam heraus, sobald zwei oder mehr Zettel ausgewählt sind.
        Beide liegen in EINEM Wrapper, damit die Bewegung sie als ein Stück
        trägt — und damit die Gruppe darin `:last-child` bleibt und ihre
        untere Rundung von der bestehenden Regel bekommt.

        Das ist ein zweiter Zugang, kein Ersatz: Am Rahmen um die Auswahl
        (`SelectionOverlay`) steht die Sammelübernahme weiterhin.
      */}
      <AnimatePresence initial={false}>
        {showApply && (
          <motion.div
            key="apply-segment"
            className="canvas-toolbar__apply-shell"
            initial={applySegmentInitial}
            animate={applySegmentEnter}
            exit={applySegmentExit}
            style={applySegmentStyle}
          >
            <div className="canvas-toolbar__divider">
              <span className="canvas-toolbar__divider-line" />
            </div>
            <div className="canvas-toolbar__group canvas-toolbar__group--apply">
              <WithTooltip side={tooltipSide} sideOffset={TOOLTIP_OFFSET} content={t('projectCanvas.note.applyMany', { count: applyCount })}>
                <button
                  aria-label={t('projectCanvas.note.applyMany', { count: applyCount })}
                  type="button"
                  className="canvas-toolbar__button"
                  onClick={onApplyTasks}
                >
                  {/* 1,5 wie im Figma (`Icon / FileText` 4916:265176). Der
                      Default 2 des Icons gilt der Options-Leiste, die bei 16px
                      rendert — hier wären 2 bei voller Größe zu fett. */}
                  <CanvasApplyTasksIcon size={24} strokeWidth={1.5} />
                </button>
              </WithTooltip>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
})
